/**
 * Plain table handling: reading delimited text, finding the header row under a preamble, and
 * writing spreadsheet-safe CSV. Cells stay strings exactly as written so "0071" keeps its zeros.
 */

/** Text from bytes: UTF-8 (BOM dropped), UTF-16 with a BOM, or Windows-1252 when UTF-8 fails. */
export function decodeText(bytes: Uint8Array): string {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes.subarray(2));
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes.subarray(2));
  const start = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0;
  try {
    return new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes.subarray(start));
  } catch {
    // Older spreadsheet exports are often Windows-1252. Latin-1 covers the accents ranch files use.
    let out = "";
    for (let i = start; i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
    return out;
  }
}

/** True when the bytes look like text rather than a binary format. */
export function looksLikeText(bytes: Uint8Array): boolean {
  const head = bytes.subarray(0, 4000);
  if (!head.length) return false;
  if (head[0] === 0xff && head[1] === 0xfe) return true;
  if (head[0] === 0xfe && head[1] === 0xff) return true;
  let control = 0;
  for (const b of head) {
    if (b === 0) return false;
    if (b < 9 || (b > 13 && b < 32)) control++;
  }
  return control / head.length < 0.02;
}

const DELIMITERS = [",", ";", "\t", "|"] as const;

/** Pick the delimiter that splits the first lines most consistently. */
export function detectDelimiter(text: string): string {
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim()).slice(0, 12);
  let best: string = ",";
  let bestScore = -1;
  for (const d of DELIMITERS) {
    const counts = lines.map((l) => splitLine(l, d).length);
    const multi = counts.filter((n) => n > 1);
    if (!multi.length) continue;
    const mode = mostCommon(multi);
    const consistent = counts.filter((n) => n === mode).length;
    const score = consistent * 10 + mode;
    if (score > bestScore) { bestScore = score; best = d; }
  }
  return best;
}

function mostCommon(values: number[]): number {
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]?.[0] ?? 0;
}

function splitLine(line: string, d: string): string[] {
  return parseDelimited(line, d)[0] ?? [];
}

/** RFC 4180 reading: quoted cells, doubled quotes, newlines inside quotes, any line ending. */
export function parseDelimited(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let i = 0;
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  while (i < src.length) {
    const ch = src[i];
    if (quoted) {
      if (ch === "\"") {
        if (src[i + 1] === "\"") { cell += "\""; i += 2; continue; }
        quoted = false; i++; continue;
      }
      cell += ch; i++; continue;
    }
    if (ch === "\"" && cell.trim() === "") { quoted = true; cell = ""; i++; continue; }
    if (ch === delimiter) { row.push(cell); cell = ""; i++; continue; }
    if (ch === "\r" || ch === "\n") {
      row.push(cell); rows.push(row); row = []; cell = "";
      if (ch === "\r" && src[i + 1] === "\n") i++;
      i++; continue;
    }
    cell += ch; i++;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.map((r) => r.map((c) => unguardCell(c.trim())));
}

/** Undo the guards spreadsheet exports (Hondo V1's and this app's) put on cells: ="0042" keeps leading zeros, a leading quote keeps a formula from running. */
export function unguardCell(cell: string): string {
  const text = /^="(.*)"$/s.exec(cell);
  if (text) return text[1].replace(/""/g, "\"");
  return /^'(?:[=+@]|-(?!\d+(?:\.\d+)?$))/.test(cell) ? cell.slice(1) : cell;
}

export const isBlank = (row: string[] | undefined): boolean => !row || row.every((c) => !c || !c.trim());
export const filled = (row: string[] | undefined): string[] => (row ?? []).filter((c) => c && c.trim());

/** Lowercase, no accents, words only: "Número de arete" -> "numero de arete". */
export function normalizeHeader(header: string): string {
  return header.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

const NUMBERISH = /^[-+]?[\d.,]+\s*[a-z%]{0,6}$/i;
const DATEISH = /^\d{1,4}[-/.]\d{1,2}([-/.]\d{1,4})?$/;

/**
 * Find the header row under any preamble ("Arroyo Ranch, September notes"). Headers are rows of
 * short labels with data below; known heading words count extra.
 */
export function findHeaderRow(rows: string[][], isKnownHeader: (cell: string) => boolean = () => false): number {
  const limit = Math.min(rows.length, 20);
  let best = 0;
  let bestScore = -Infinity;
  const width = Math.max(1, ...rows.slice(0, 50).map((r) => filled(r).length));
  for (let i = 0; i < limit; i++) {
    const cells = filled(rows[i]);
    if (!cells.length) continue;
    if (cells.length < 2 && width > 1) continue;
    const labels = cells.filter((c) => !NUMBERISH.test(c) && !DATEISH.test(c) && c.length <= 48);
    const known = cells.filter((c) => isKnownHeader(c)).length;
    const below = rows.slice(i + 1, i + 6).filter((r) => filled(r).length >= Math.max(1, Math.floor(cells.length / 2))).length;
    const score = labels.length + known * 2 + below * 0.6 + (cells.length / width) * 2 - i * 0.15 - (cells.length - labels.length) * 1.5;
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best;
}

/** Unique, non-empty headings so every column can be named in plans and kept values. */
export function headersOf(rows: string[][], headerRow: number): string[] {
  const raw = rows[headerRow] ?? [];
  const width = Math.max(raw.length, ...rows.slice(headerRow + 1, headerRow + 200).map((r) => r.length));
  const seen = new Map<string, number>();
  const out: string[] = [];
  for (let i = 0; i < width; i++) {
    const base = (raw[i] ?? "").trim() || `Column ${columnLetter(i)}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    out.push(n > 1 ? `${base} (${n})` : base);
  }
  return out;
}

export function columnLetter(index: number): string {
  let n = index + 1;
  let s = "";
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

/** Data rows below the header, with their 1-based row numbers in the original sheet. */
export function dataRows(rows: string[][], headerRow: number): Array<{ number: number; cells: string[] }> {
  const out: Array<{ number: number; cells: string[] }> = [];
  for (let i = headerRow + 1; i < rows.length; i++) out.push({ number: i + 1, cells: rows[i] ?? [] });
  return out;
}

// ---------------------------------------------------------------- writing

/** A cell a spreadsheet would run as a formula gets a leading quote. Plain negative numbers stay. */
const FORMULA_START = /^[=+@\t\r]|^-(?!\d+(?:\.\d+)?$)/;

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  let s = typeof value === "object" ? JSON.stringify(value) : String(value);
  if (typeof value === "string" && FORMULA_START.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, "\"\"")}"` : s;
}

/** CSV with a byte order mark so Excel reads accents and ñ correctly. */
export function toCsv(columns: string[], rows: Array<Record<string, unknown>>): string {
  return "﻿" + [columns.map(csvCell).join(","), ...rows.map((r) => columns.map((c) => csvCell(r[c])).join(","))].join("\r\n") + "\r\n";
}
